import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { pwaService } from '../services/pwaService';

interface AddToHomeScreenProps {
  onClose?: () => void;
  onInstall?: () => void;
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const AddToHomeScreen: React.FC<AddToHomeScreenProps> = ({ onClose, onInstall }) => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  // Device info and prompt state management
  useEffect(() => {
    const updateDeviceInfo = () => {
      const deviceInfo = pwaService.getDeviceInfo();
      
      setIsIOS(deviceInfo.isIOS);
      setIsAndroid(deviceInfo.isAndroid);
      setIsStandalone(deviceInfo.isStandalone);

      // If already installed, don't show prompt
      if (deviceInfo.isInstalled) {
        console.log('📱 ScanVCF PWA already installed - not showing prompt');
        setShowPrompt(false);
        return;
      }

      return deviceInfo;
    };

    // Initial device info update
    updateDeviceInfo();

    // Install prompt state detection
    const unsubscribe = pwaService.onInstallPromptChange((prompt) => {
      setDeferredPrompt(prompt);

      // Get fresh device info to check current installation status
      const currentDeviceInfo = pwaService.getDeviceInfo();
      if (currentDeviceInfo.isInstalled) {
        setShowPrompt(false);
        return;
      }

      // iOS manual prompt and Android beforeinstallprompt modal are both decided here.
      const { showModal } = pwaService.shouldShowAnyPrompt();
      setShowPrompt(showModal);
    });

    return unsubscribe;
  }, []);

  // Removed duplicate prompt handling logic - now managed by pwaService

  // Installation handling
  const handleInstall = useCallback(async () => {
    const success = await pwaService.install();
    if (success) {
      onInstall?.();
    }
    setShowPrompt(false);
  }, [onInstall]);

  // Close handling
  const handleClose = useCallback(() => {
    setShowPrompt(false);
    pwaService.markPromptShown();
    onClose?.();
  }, [onClose]);

  // Removed prompt display condition check logic - now managed by pwaService

  // Test: Force display via URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const forceShow = urlParams.get('pwa_test') === 'true';
  
  if ((!showPrompt && !forceShow) || isStandalone) {
    return null;
  }

  const getDeviceName = () => {
    if (isIOS) {
      return /ipad/.test(navigator.userAgent.toLowerCase()) ? 'iPad' : 'iPhone';
    }
    return 'Android';
  };

  const getInstallMessage = () => {
    const device = getDeviceName();
    
    if (isIOS) {
      return `Install this web app on your ${device}: tap the share button and then "Add to Home Screen".`;
    } else {
      return `Install this web app on your ${device}: tap the menu button and then "Add to homescreen".`;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm mx-4 p-6 relative">
        {/* 닫기 버튼 */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-xl"
          aria-label={t('common.close', 'Close')}
        >
          ×
        </button>

        {/* 아이콘 */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
            </svg>
          </div>
        </div>

        {/* 제목 */}
        <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
          Install ScanVCF
        </h3>

        {/* 메시지 */}
        <p className="text-sm text-gray-600 text-center mb-6 leading-relaxed">
          {getInstallMessage()}
        </p>

        {/* 버튼들 */}
        <div className="flex flex-col space-y-3">
          {isAndroid && deferredPrompt ? (
            <button
              onClick={handleInstall}
              className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-600 transition-colors"
            >
              Install App
            </button>
          ) : (
            <button
              onClick={handleClose}
              className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-600 transition-colors"
            >
              Got it!
            </button>
          )}
          
          <button
            onClick={handleClose}
            className="w-full text-gray-500 py-2 px-4 rounded-lg font-medium hover:text-gray-700 transition-colors"
          >
            Not now
          </button>
        </div>

        {/* 추가 정보 */}
        <div className="mt-4 text-xs text-gray-400 text-center">
          Access your business cards faster with the app
        </div>
      </div>
    </div>
  );
};

export default AddToHomeScreen;
